/* eslint max-lines:0 */
import log from 'loglevel';
import Documents from '../../../services/Documents';
import DocumentsInterface from '../../../interfaces/DocumentsInterface';
import { PagedMetaInterface } from '../../../entities/PagedMeta';
import { toast } from 'react-toastify';
import FilteredOptionsInterface, { FilterInterface } from '../../../entities/FilteredOptions';
import FranchiseeInterface from '../../../interfaces/FranchiseeInterface';
import DocumentsFolders from '../../../services/DocumentsFolders';
import DocumentsFoldersInterface from '../../../interfaces/DocumentsFoldersInterface';
import { createOptionsPage, OptionsPagedInterface } from '../../../entities/OptionsPaged';
import PermissionGroups from '../../../services/PermissionGroups';
import { isAdmin } from '../../../helpers';

export type FilteredOptionsType = FilteredOptionsInterface & {
  actions: any[];
  options: OptionsPagedInterface | undefined;
  filters: FilterInterface[] | undefined;
};

export type DocumentsGroupPermissions = {
  groupname: string;
  module: string;
  function: string;
};

export type DocumentsState = {
  showFoldersForm: boolean;
  showDocumentsForm: boolean;
  shouldRefresh: number;
  title: string;
  hash: any;
  location: string;
  user: any;
  school: FranchiseeInterface | undefined;
  filterSearch: string | null;
  token: string | undefined;
  pageMetadata: PagedMetaInterface | undefined;
  documentsCards: DocumentsInterface[];
  foldersCards: DocumentsFoldersInterface[];
  isLoading: boolean;
  moduleTitle: string;
  filteredOptions: FilteredOptionsType;
  folderModal?: DocumentsFoldersInterface;
  fileModal?: DocumentsInterface;
  hoverIndex?: number;
  groupPermissions?: DocumentsGroupPermissions[];
  currentFolder?: DocumentsFoldersInterface;
};

export const initialDocumentsState: DocumentsState = {
  showFoldersForm: false,
  showDocumentsForm: false,
  shouldRefresh: 0,
  title: '',
  hash: null,
  location: '',
  user: null,
  school: undefined,
  filterSearch: null,
  token: undefined,
  pageMetadata: undefined,
  documentsCards: [],
  foldersCards: [],
  isLoading: false,
  moduleTitle: '',
  filteredOptions: {
    actions: [],
    options: undefined,
    filters: [],
  },
};

export enum DocumentsActionTypes {
  MODAL_DOCUMENTS_SHOW = 'MODAL_DOCUMENTS_SHOW',
  MODAL_FOLDER_SHOW = 'MODAL_FOLDER_SHOW',
  REFRESH = 'REFRESH',
  SET_TITLE = 'SET_TITLE',
  SET_LOCATION = 'SET_LOCATION',
  SET_HASH = 'SET_HASH',
  SET_USER = 'SET_USER',
  SET_SCHOOL = 'SET_SCHOOL',
  SET_FILTER_SEARCH = 'SET_FILTER_SEARCH',
  SET_TOKEN = 'SET_TOKEN',
  SET_DOCUMENTS_CARDS = 'SET_DOCUMENTS_CARDS',
  SET_PAGE_METADATA = 'SET_PAGE_METADATA',
  SET_FOLDERS_CARDS = 'SET_FOLDERS_CARDS',
  SET_LOADING = 'SET_LOADING',
  CLEAR_DATA = 'CLEAR_DATA',
  SET_MODULE_TITLE = 'SET_MODULE_TITLE',
  SET_HOVER_INDEX = 'SET_HOVER_INDEX',
  SET_GROUP_PERMISSIONS = 'SET_GROUP_PERMISSIONS',
  SET_CURRENT_FOLDER = 'SET_CURRENT_FOLDER',
  SET_FOLDERS_AND_CARDS = 'SET_FOLDERS_AND_CARDS',
}

export type DocumentsAction = {
  type: DocumentsActionTypes;
  payload: undefined | null | string | any;
};

export class DocumentsStateManager {
  public state: DocumentsState = initialDocumentsState;
  protected dispatch: React.Dispatch<any>;
  protected runningLoad = false;

  constructor() {
    log.debug('DocumentsStateManager constructor');
    this.dispatch = () => {
      throw new Error('Dispatch is not set');
    };
  }

  public reducer = (state: DocumentsState, action: DocumentsAction): DocumentsState => {
    switch (action.type) {
      case DocumentsActionTypes.MODAL_FOLDER_SHOW:
        return { ...state, showFoldersForm: !state.showFoldersForm, folderModal: action.payload };
      case DocumentsActionTypes.MODAL_DOCUMENTS_SHOW:
        return { ...state, showDocumentsForm: !state.showDocumentsForm, fileModal: action.payload };
      case DocumentsActionTypes.REFRESH:
        return { ...state, shouldRefresh: state.shouldRefresh + 1 };
      case DocumentsActionTypes.SET_TITLE:
        return { ...state, title: action.payload ?? '' };
      case DocumentsActionTypes.SET_HASH:
        return { ...state, hash: action.payload ?? '' };
      case DocumentsActionTypes.SET_LOCATION:
        return { ...state, location: action.payload ?? '' };
      case DocumentsActionTypes.SET_USER:
        return { ...state, user: action.payload ?? '' };
      case DocumentsActionTypes.SET_SCHOOL:
        return { ...state, school: action.payload ?? '' };
      case DocumentsActionTypes.SET_FILTER_SEARCH:
        return { ...state, filterSearch: action.payload ?? '' };
      case DocumentsActionTypes.SET_TOKEN:
        return { ...state, token: action.payload ?? '' };
      case DocumentsActionTypes.SET_PAGE_METADATA:
        return {
          ...state,
          filteredOptions: { actions: [], options: createOptionsPage(action.payload), filters: undefined },
          pageMetadata: action.payload ?? '',
        };
      case DocumentsActionTypes.SET_DOCUMENTS_CARDS:
        return { ...state, documentsCards: action.payload ?? [], hoverIndex: undefined };
      case DocumentsActionTypes.SET_FOLDERS_CARDS:
        return { ...state, foldersCards: action.payload ?? [] };
      case DocumentsActionTypes.SET_LOADING:
        return { ...state, isLoading: action.payload ?? false };
      case DocumentsActionTypes.CLEAR_DATA:
        return { ...state, documentsCards: [], foldersCards: [], isLoading: false };
      case DocumentsActionTypes.SET_MODULE_TITLE:
        return { ...state, moduleTitle: action.payload ?? '' };
      case DocumentsActionTypes.SET_HOVER_INDEX:
        return { ...state, hoverIndex: action.payload };
      case DocumentsActionTypes.SET_GROUP_PERMISSIONS:
        return { ...state, groupPermissions: action.payload };
      case DocumentsActionTypes.SET_CURRENT_FOLDER:
        return { ...state, currentFolder: action.payload };
      case DocumentsActionTypes.SET_FOLDERS_AND_CARDS:
        return { ...state, documentsCards: action.payload.documents, foldersCards: action.payload.folders };
      default:
        return state;
    }
  };

  public setReducer(state: DocumentsState, dispatch: React.Dispatch<any>) {
    this.state = state;
    this.dispatch = dispatch;
  }

  public modalFolderShow(folder: DocumentsFoldersInterface | undefined = undefined) {
    this.dispatch({ type: DocumentsActionTypes.MODAL_FOLDER_SHOW, payload: folder });
  }

  public modalFilesShow(file: DocumentsInterface | undefined = undefined) {
    this.dispatch({ type: DocumentsActionTypes.MODAL_DOCUMENTS_SHOW, payload: file });
  }

  public doRefresh() {
    this.dispatch({ type: DocumentsActionTypes.REFRESH });
  }

  public setTitle(title: string) {
    if (title === this.state.title) return;
    this.dispatch({ type: DocumentsActionTypes.SET_TITLE, payload: title });
  }

  public setLocation(location: string) {
    if (location === this.state.location) return;
    this.dispatch({ type: DocumentsActionTypes.SET_LOCATION, payload: location });
  }

  public setHash(hash: any) {
    if (hash === this.state.hash) return;
    this.dispatch({ type: DocumentsActionTypes.SET_HASH, payload: hash });
  }

  public setUser(user: any) {
    if (user === this.state.user) return;
    this.dispatch({ type: DocumentsActionTypes.SET_USER, payload: user });
  }

  public setSchool(school: FranchiseeInterface) {
    if (school === this.state.school) return;
    this.dispatch({ type: DocumentsActionTypes.SET_SCHOOL, payload: school });
  }

  public setFilterSearch(filter: string) {
    if (filter === this.state.filterSearch) return;
    this.dispatch({ type: DocumentsActionTypes.SET_FILTER_SEARCH, payload: filter });
  }

  public setToken(token: string | undefined) {
    if (token === this.state.token) return;
    this.dispatch({ type: DocumentsActionTypes.SET_TOKEN, payload: token });
  }

  public setDocumentsCards(cards: DocumentsInterface[]) {
    if (cards === this.state.documentsCards) return;
    this.dispatch({ type: DocumentsActionTypes.SET_DOCUMENTS_CARDS, payload: cards });
  }

  public setFoldersCards(cards: DocumentsFoldersInterface[]) {
    if (cards === this.state.foldersCards) return;
    this.dispatch({ type: DocumentsActionTypes.SET_FOLDERS_CARDS, payload: cards });
  }

  public setFoldersAndCards(folders: DocumentsFoldersInterface[], documents: DocumentsInterface[]) {
    if (folders === this.state.foldersCards && documents === this.state.documentsCards) return;
    this.dispatch({ type: DocumentsActionTypes.SET_FOLDERS_AND_CARDS, payload: { folders, documents } });
  }

  public setPageMetadata(pageMetadata: PagedMetaInterface) {
    if (pageMetadata === this.state.pageMetadata) return;
    this.dispatch({ type: DocumentsActionTypes.SET_PAGE_METADATA, payload: pageMetadata });
  }

  public setModuleTitle(title: string) {
    if (title === this.state.moduleTitle) return;
    this.dispatch({ type: DocumentsActionTypes.SET_MODULE_TITLE, payload: title });
  }

  public setHoverIndex(index: number) {
    if (index === this.state.hoverIndex) return;
    this.dispatch({ type: DocumentsActionTypes.SET_HOVER_INDEX, payload: index });
  }

  public setLoading(isLoading: boolean) {
    if (isLoading === this.state.isLoading || this.runningLoad) return;
    this.dispatch({ type: DocumentsActionTypes.SET_LOADING, payload: isLoading });
  }

  public setCurrentFolder(newFolder: DocumentsFoldersInterface) {
    if (newFolder.id === this.state.currentFolder?.id) return;
    this.dispatch({ type: DocumentsActionTypes.SET_CURRENT_FOLDER, payload: newFolder });
  }

  public setPermissionGroups(permissions: DocumentsGroupPermissions[]) {
    if (permissions === this.state.groupPermissions) return;
    this.dispatch({ type: DocumentsActionTypes.SET_GROUP_PERMISSIONS, payload: permissions });
  }

  public clearData() {
    this.dispatch({ type: DocumentsActionTypes.CLEAR_DATA });
  }

  private findPermission(functionToSearch: string, moduleToSearch: string) {
    if (isAdmin(this.state.user)) return true;
    return this.state.groupPermissions?.find((x) => {
      return (
        x.groupname === this.state.user.groupPermission.name &&
        x.function === functionToSearch &&
        x.module === moduleToSearch
      );
    });
  }

  private getModule() {
    let module = this.state.title;
    if (module === 'Resources') {
      module = 'Documents';
    }
    return module;
  }

  public hasFolderShowPermission(): boolean {
    return !!this.findPermission('Show', this.getModule());
  }
  public hasFolderEditPermission(): boolean {
    return !!this.findPermission('Create', this.getModule());
  }
  public hasFolderDeletePermission(): boolean {
    return !!this.findPermission('Delete', this.getModule());
  }
  public hasDocumentEditPermission(): boolean {
    return !!this.findPermission('Create', this.getModule());
  }
  public hasDocumentDeletePermission(): boolean {
    return !!this.findPermission('Delete', this.getModule());
  }
  public hasDocumentShowPermission(): boolean {
    return !!this.findPermission('Show', this.getModule());
  }

  public hasFolderAccess(folderId: string): boolean {
    return (
      this.state.folderModal?.groupPermissions.find((x: string) => x === folderId) !== undefined ||
      !!this.findPermission('Show', this.getModule())
    );
  }

  public getPermissionsGroups(): string[] {
    return [...new Set(this.state.groupPermissions?.map((x) => x.groupname) ?? [])];
  }

  public loadDocumentsList = async (filteredOptions: FilteredOptionsInterface, hashFolderId: string) => {
    try {
      this.runningLoad = true;
      log.error('Starting load list...');
      let moduleName = '';
      if (this.state.location == '/curriculum') {
        moduleName = 'Curriculum';
      } else if (this.state.location == '/resource') {
        moduleName = 'Resources';
      }

      let filterFolderId;
      if (!this.state.filterSearch || this.state.filterSearch == '') {
        filterFolderId = {
          field: 'documentsFoldersId',
          operation: 'hash',
          value: hashFolderId,
        };
      } else {
        filterFolderId = {
          field: 'documentsFolders.typeFile',
          operation: 'hash',
          value: `${moduleName}`,
        };
      }

      const filterSchool = {
        field: 'schoolId',
        operation: 'hash',
        value: this.state.school?.value,
      };

      const filterSearch = {
        field: 'name',
        operation: 'ilike',
        value: `%${this.state.filterSearch ? this.state.filterSearch : ''}%`,
      };

      filteredOptions.filters = this.state.filterSearch
        ? [filterFolderId, filterSearch, filterSchool]
        : [filterFolderId, filterSchool];

      const result = await Documents(this.state.token || '').list(filteredOptions);
      const data: DocumentsInterface[] = result.data;

      this.runningLoad = false;
      log.debug(`Received data:${data}`);
      const meta: PagedMetaInterface = result.meta;
      if (meta && JSON.stringify(meta) !== JSON.stringify(this.state.pageMetadata)) {
        this.setPageMetadata(meta);
      }
      if (data && JSON.stringify(data) !== JSON.stringify(this.state.documentsCards)) {
        return data;
      }
      return null;
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      toast.error(message);
      return null;
    } finally {
      this.runningLoad = false;
    }
  };

  public loadFolderList = async (
    filteredOptions: FilteredOptionsInterface,
    titleModule: string,
    hashFolderId: string,
  ) => {
    try {
      this.runningLoad = true;
      log.debug('Starting load list(debug)...');
      let filterFolderId;
      if (!this.state.filterSearch) {
        if (hashFolderId) {
          filterFolderId = {
            field: 'documentsFoldersId',
            operation: 'hash',
            value: hashFolderId,
          };
        } else {
          filterFolderId = {
            field: 'documentsFoldersId',
            operation: '=',
            value: '0',
          };
        }
      } else {
        filterFolderId = {
          field: 'name',
          operation: 'ilike',
          value: `%${this.state.filterSearch ? this.state.filterSearch : ''}%`,
        };
      }
      const filter = {
        field: 'typeFile',
        operation: '=',
        value: titleModule,
      };

      const filterSchool = {
        field: 'schoolId',
        operation: 'hash',
        value: this.state?.school?.value,
      };

      filteredOptions.filters = [filter, filterFolderId, filterSchool];

      const result = await DocumentsFolders(this.state.token || '').list(filteredOptions);
      if (result.error == 'NOT_IN_SCHOOL') {
        toast.error('It is not possible to access the documents without being on the school network.');
      }
      this.runningLoad = false;
      const data: DocumentsFoldersInterface[] = result.data;

      const meta: PagedMetaInterface = result.meta;
      if (meta && JSON.stringify(meta) !== JSON.stringify(this.state.pageMetadata)) {
        this.setPageMetadata(meta);
      }
      if (
        data &&
        JSON.stringify(
          data.map((x) => {
            return {
              id: x.id,
              name: x.name,
              groupPermissions: x.groupPermissions,
            };
          }),
        ) !==
          (JSON.stringify(
            this.state?.foldersCards?.map((x) => {
              return { id: x.id, name: x.name, groupPermissions: x.groupPermissions };
            }),
          ) ?? '')
      ) {
        return data;
      }
      return null;
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      toast.error(message);
      return null;
    } finally {
      this.runningLoad = false;
    }
  };

  public async loadFoldersAndDocuments(
    filteredOptions: FilteredOptionsInterface,
    titleModule: string,
    hashFolderId: string,
  ) {
    const folders = (await this.loadFolderList(filteredOptions, titleModule, hashFolderId)) ?? this.state.foldersCards;
    const documents = (await this.loadDocumentsList(filteredOptions, hashFolderId)) ?? this.state.documentsCards;
    this.setFoldersAndCards(folders, documents);
  }

  public async deleteFile(id: string) {
    try {
      await Documents(this.state.token || '').delete(id);
      toast.success(`Deleted successfully!`);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || `Error deleting item: ${id}`);
      return toast.error(message);
    }
  }

  public async deleteFolder(id: string) {
    try {
      await DocumentsFolders(this.state.token || '').delete(id);
      toast.success(`Deleted successfully!`);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || `Error deleting item: ${id}`);
      return toast.error(message);
    }
  }

  public async loadGroupPermissions(module: string) {
    try {
      if (module === 'Resources') {
        module = 'Documents';
      }
      const permissions = await PermissionGroups(this.state.token || '').listPerModule(module);
      this.setPermissionGroups(permissions);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || `Error setting permissions for module: ${module}`);
      return toast.error(message);
    }
  }

  public async refreshLocalFolder(_hashId: string) {
    let hashId = _hashId;
    if (!hashId) {
      return;
    }
    if (this.state.currentFolder?.id === hashId) {
      return;
    }
    // if hashId starts with a pound #, remove it
    if (hashId.startsWith('#')) {
      hashId = hashId.substring(1);
    }
    const currentFolder = (await DocumentsFolders(this.state.token || '').show(hashId))
      .data as DocumentsFoldersInterface;
    this.setCurrentFolder(currentFolder);
  }
}
